import { z } from "zod";
import { eventDocumentSchema } from "@event-planner/shared";

/**
 * Admin form schema for the `events` collection.
 *
 * Per ADR-083, the canonical document shape lives at
 * `packages/shared/src/schemas/event.ts`. This file layers the form-only
 * concerns on top:
 *  - User-facing validation messages (`min(1, "Event name is required")`)
 *  - Cross-field rules (`endDate >= startDate`)
 *  - Constrained per-field rules (e.g., hex regex on color fields)
 *  - Omission of server-set / deferred fields the form doesn't touch
 *    (`createdByUserId`, `customTheme`, `customFonts`)
 *
 * Per ADR-120, session categories reference one default Audience via
 * `defaultAudienceId` (nullable). When the organizer creates a new
 * session of this category, the form pre-fills the audience picker
 * with this audience; existing sessions are unaffected by category
 * config changes.
 */

const sessionTypeConfigSchema = z.object({
  id: z.string().min(1, "ID is required"),
  name: z.string().min(1, "Name is required"),
  backgroundColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, "Must be a valid hex color (e.g., #5C6BC0)"),
  textColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, "Must be a valid hex color (e.g., #FFFFFF)"),
  iconName: z.string().nullable(),
  defaultAudienceId: z.string().nullable(),
});

const faqEntrySchema = z.object({
  id: z.string().min(1, "ID is required"),
  question: z.string().min(1, "Question is required"),
  answer: z.string().min(1, "Answer is required"),
});

const eventConfigSchema = z.object({
  // Label overrides — deferred, always null for now
  agendaLabel: z.string().nullable(),
  myScheduleLabel: z.string().nullable(),
  speakersLabel: z.string().nullable(),
  sessionsLabel: z.string().nullable(),
  sessionTypes: z.array(sessionTypeConfigSchema),
  faqs: z.array(faqEntrySchema),
});

// Form view: drop server-set + deferred fields, then layer form-specific
// validation on the remaining fields via shape destructure.
const eventDocumentFormBase = eventDocumentSchema.omit({
  createdByUserId: true,
  customTheme: true,
  customFonts: true,
  config: true,
});

export const eventSchema = z
  .object({
    ...eventDocumentFormBase.shape,
    name: z.string().min(1, "Event name is required"),
    startDate: z.string().min(1, "Start date is required"),
    endDate: z.string().min(1, "End date is required"),
    timezone: z.string().min(1, "Timezone is required"),
    heroOverlayColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, "Must be a valid hex color")
      .nullable(),
    heroOverlayOpacity: z.number().min(0).max(1).nullable(),
    config: eventConfigSchema,
  })
  .refine(
    (data) => data.endDate >= data.startDate,
    {
      message: "End date must be on or after start date",
      path: ["endDate"],
    },
  );

export type EventFormData = z.infer<typeof eventSchema>;
